import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { DocxSurfaceHandle } from "../draftEditor/surface";

import {
  citationLocatorFor,
  type CitationLocator,
  type LocatedCitation
} from "./locator";
import type { CitationRef } from "./model";

export type CitationHit = CitationRef & {
  /** Identity of the hovered citation (node + page), stable across repaints. */
  key: string;
  /** Viewport rect of the hovered line fragment; the hover card anchors here. */
  rect: DOMRect;
  /** The painted fragment under the pointer (replaced on every repaint). */
  fragment: Element;
  /** How the card was opened: pointer hover or the caret inside the control. */
  via: "pointer" | "caret";
};

export type CitationChrome = {
  /** The citation under the pointer (or at the caret), after the open delay. */
  hit: CitationHit | null;
  /** Keep the card open while the pointer is over it (`true`) and let it close again (`false`). */
  hold: (on: boolean) => void;
  /** Close now (Escape, an action taken). */
  dismiss: () => void;
  /** Node ids of every citation currently painted in the host. */
  citedNodeIds: string[];
};

type Options = {
  /** The mounted editor; decides how citations are located. */
  surface?: DocxSurfaceHandle | null;
  /** Override the locator entirely (tests). */
  locator?: (host: HTMLElement) => CitationLocator;
  openDelay?: number;
  closeDelay?: number;
  /** Cmd/Ctrl-click on a citation. */
  onActivate?: (ref: CitationRef) => void;
};

const SLACK_PX = 2;

const contains = (rect: DOMRect, x: number, y: number) =>
  x >= rect.left - SLACK_PX &&
  x <= rect.right + SLACK_PX &&
  y >= rect.top - SLACK_PX &&
  y <= rect.bottom + SLACK_PX;

type Located = Omit<CitationHit, "via"> & { citation: LocatedCitation };

/** The citation whose painted fragment is under (x, y), if any. */
export const hitTestCitation = (
  locator: CitationLocator,
  x: number,
  y: number
): Located | null => {
  for (const citation of locator.list()) {
    for (const fragment of locator.fragments(citation)) {
      const rect = fragment.getBoundingClientRect();
      const painted = rect.width > 0 || rect.height > 0;
      if (painted && contains(rect, x, y)) {
        const { nodeId, page, key } = citation;
        return { nodeId, page, key, rect, fragment, citation };
      }
    }
  }
  return null;
};

/** The citation the caret sits in. */
const caretCitation = (locator: CitationLocator): Located | null => {
  const citation = locator.atCaret();
  if (!citation) return null;
  const [fragment] = locator.fragments(citation);
  if (!fragment) return null;
  const { nodeId, page, key } = citation;
  return {
    nodeId,
    page,
    key,
    rect: fragment.getBoundingClientRect(),
    fragment,
    citation
  };
};

const uniqueNodeIds = (locator: CitationLocator): string[] => {
  const seen = new Set<string>();
  for (const citation of locator.list()) seen.add(citation.nodeId);
  return [...seen];
};

/** Our own hover flag; set on every fragment of the hovered citation and on the host. */
const HOVER_ATTR = "data-citation-hover";

const markHovered = (
  host: HTMLElement,
  locator: CitationLocator,
  citation: LocatedCitation | null
) => {
  const next = new Set(citation ? locator.fragments(citation) : []);
  for (const previous of Array.from(host.querySelectorAll(`[${HOVER_ATTR}]`))) {
    if (!next.has(previous)) previous.removeAttribute(HOVER_ATTR);
  }
  for (const fragment of next) fragment.setAttribute(HOVER_ATTR, "");
  host.toggleAttribute(HOVER_ATTR, next.size > 0);
};

const sameIds = (a: string[], b: string[]) =>
  a.length === b.length && a.every((id, i) => id === b[i]);

/**
 * Hover detection for exhibit citations in the docx editor.
 *
 * Nothing in either editor's API reports "pointer over control", so this
 * delegates `pointermove` on the host and hit-tests the citations' painted
 * line fragments itself (a handful per document; one `getBoundingClientRect`
 * each, once per animation frame). Where the fragments come from is the
 * locator's business (see `locator.ts`). Radix-style timing: a short delay
 * before opening, a grace period before closing, and `hold` while the card
 * itself is hovered. The caret landing inside a citation (keyboard users)
 * opens the card too.
 */
export const useCitationChrome = (
  host: HTMLElement | null,
  {
    surface = null,
    locator: makeLocator,
    openDelay = 220,
    closeDelay = 180,
    onActivate
  }: Options = {}
): CitationChrome => {
  const [hit, setHit] = useState<CitationHit | null>(null);
  const [nodeIds, setNodeIds] = useState<string[]>([]);
  const hitRef = useRef<CitationHit | null>(null);
  const holdRef = useRef(false);
  const openTimer = useRef<number | null>(null);
  const closeTimer = useRef<number | null>(null);
  const frame = useRef<number | null>(null);
  const pointer = useRef<{ x: number; y: number } | null>(null);
  const activateRef = useRef(onActivate);
  activateRef.current = onActivate;

  const locator = useMemo(
    () =>
      host
        ? makeLocator
          ? makeLocator(host)
          : citationLocatorFor(host, surface)
        : null,
    [host, makeLocator, surface]
  );

  const commit = useCallback((next: CitationHit | null) => {
    hitRef.current = next;
    setHit(next);
  }, []);

  const clearTimers = useCallback(() => {
    if (openTimer.current !== null) window.clearTimeout(openTimer.current);
    if (closeTimer.current !== null) window.clearTimeout(closeTimer.current);
    openTimer.current = null;
    closeTimer.current = null;
  }, []);

  const scheduleClose = useCallback(() => {
    if (openTimer.current !== null) {
      window.clearTimeout(openTimer.current);
      openTimer.current = null;
    }
    if (holdRef.current || !hitRef.current || closeTimer.current !== null)
      return;
    closeTimer.current = window.setTimeout(() => {
      closeTimer.current = null;
      if (!holdRef.current) commit(null);
    }, closeDelay);
  }, [closeDelay, commit]);

  const scheduleOpen = useCallback(
    (next: CitationHit) => {
      if (closeTimer.current !== null) {
        window.clearTimeout(closeTimer.current);
        closeTimer.current = null;
      }
      const { current } = hitRef;
      if (current && current.key === next.key) {
        // Same citation, maybe another line fragment: follow it without a delay.
        if (
          current.rect.top !== next.rect.top ||
          current.rect.left !== next.rect.left
        ) {
          commit(next);
        }
        return;
      }
      if (openTimer.current !== null) return;
      // Already showing another citation: switch straight over.
      if (current) {
        commit(next);
        return;
      }
      openTimer.current = window.setTimeout(() => {
        openTimer.current = null;
        commit(next);
      }, openDelay);
    },
    [commit, openDelay]
  );

  const hold = useCallback(
    (on: boolean) => {
      holdRef.current = on;
      if (on) {
        if (closeTimer.current !== null) {
          window.clearTimeout(closeTimer.current);
          closeTimer.current = null;
        }
      } else if (
        !pointer.current ||
        !locator ||
        !hitTestCitation(locator, pointer.current.x, pointer.current.y)
      ) {
        scheduleClose();
      }
    },
    [locator, scheduleClose]
  );

  const dismiss = useCallback(() => {
    clearTimers();
    holdRef.current = false;
    commit(null);
  }, [clearTimers, commit]);

  useEffect(() => {
    if (!host || !locator) return undefined;

    const toHit = (located: Located, via: CitationHit["via"]): CitationHit => ({
      nodeId: located.nodeId,
      page: located.page,
      key: located.key,
      rect: located.rect,
      fragment: located.fragment,
      via
    });

    const evaluatePointer = () => {
      frame.current = null;
      const at = pointer.current;
      if (!at) return;
      const found = hitTestCitation(locator, at.x, at.y);
      markHovered(host, locator, found?.citation ?? null);
      if (found) scheduleOpen(toHit(found, "pointer"));
      else if (hitRef.current?.via === "pointer") scheduleClose();
    };

    const onPointerMove = (event: PointerEvent) => {
      pointer.current = { x: event.clientX, y: event.clientY };
      if (frame.current === null)
        frame.current = window.requestAnimationFrame(evaluatePointer);
    };

    const onPointerLeave = () => {
      pointer.current = null;
      markHovered(host, locator, null);
      if (hitRef.current?.via === "pointer") scheduleClose();
    };

    const onPointerDown = (event: PointerEvent) => {
      if (!(event.metaKey || event.ctrlKey)) return;
      const found = hitTestCitation(locator, event.clientX, event.clientY);
      if (!found) return;
      event.preventDefault();
      activateRef.current?.({ nodeId: found.nodeId, page: found.page });
    };

    // The page repaints while scrolling; anchors move, so the card lets go.
    const onScroll = () => {
      if (hitRef.current) dismiss();
    };

    // The editor rebuilds the painted DOM on every layout pass and the caret
    // moves with every selection change. Both are cheap to observe; the work
    // happens once per frame at most.
    let mutationFrame: number | null = null;
    const evaluateDocument = () => {
      mutationFrame = null;
      locator.decorate();
      const ids = uniqueNodeIds(locator);
      setNodeIds((previous) => (sameIds(previous, ids) ? previous : ids));
      const atCaret = caretCitation(locator);
      if (atCaret) {
        if (!hitRef.current || hitRef.current.via === "caret") {
          if (closeTimer.current !== null) {
            window.clearTimeout(closeTimer.current);
            closeTimer.current = null;
          }
          if (
            hitRef.current?.key !== atCaret.key ||
            hitRef.current.rect.top !== atCaret.rect.top
          ) {
            commit(toHit(atCaret, "caret"));
          }
        }
      } else if (hitRef.current?.via === "caret") {
        scheduleClose();
      } else if (hitRef.current?.via === "pointer" && pointer.current) {
        // Repaint under a hovering pointer: re-anchor or close.
        evaluatePointer();
      }
    };
    const onMutation = () => {
      if (mutationFrame !== null) return;
      mutationFrame = window.requestAnimationFrame(evaluateDocument);
    };
    const observer = new MutationObserver(onMutation);
    observer.observe(host, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: locator.attributeFilter
    });
    onMutation();

    // The model locator learns about caret moves from selection changes, which
    // do not necessarily touch the DOM; a selectionchange listener covers them.
    const onSelectionChange = () => onMutation();

    host.addEventListener("pointermove", onPointerMove);
    host.addEventListener("pointerleave", onPointerLeave);
    host.addEventListener("pointerdown", onPointerDown);
    host.addEventListener("scroll", onScroll, true);
    host.ownerDocument.addEventListener("selectionchange", onSelectionChange);
    return () => {
      observer.disconnect();
      if (mutationFrame !== null) window.cancelAnimationFrame(mutationFrame);
      if (frame.current !== null) window.cancelAnimationFrame(frame.current);
      frame.current = null;
      host.removeEventListener("pointermove", onPointerMove);
      host.removeEventListener("pointerleave", onPointerLeave);
      host.removeEventListener("pointerdown", onPointerDown);
      host.removeEventListener("scroll", onScroll, true);
      host.ownerDocument.removeEventListener(
        "selectionchange",
        onSelectionChange
      );
      markHovered(host, locator, null);
      clearTimers();
      hitRef.current = null;
      setHit(null);
    };
  }, [
    host,
    locator,
    clearTimers,
    commit,
    dismiss,
    scheduleClose,
    scheduleOpen
  ]);

  return useMemo(
    () => ({ hit, hold, dismiss, citedNodeIds: nodeIds }),
    [hit, hold, dismiss, nodeIds]
  );
};
